import type {
  JourneyLeg,
  JourneyPlan,
  LineColors,
} from '../../../arcticapi/types';
import {
  formatTransportMode,
  resolveJourneyServiceLabel,
  titleCaseTransportMode,
  OPERATOR_HEADSIGN_MODES,
} from '../../../transportModes';
import {
  buildItinerarySteps as buildItineraryStepsInternal,
  type ItineraryStep,
} from './itinerarySteps';

// TODO: update these to match https://passenger.atlassian.net/wiki/spaces/PASSENGER/pages/151322633/Patterns

/**
 * Formats an ISO date string into a 24-hour time string (HH:mm) using the en-GB locale.
 * Returns the original string if the date cannot be parsed.
 *
 * @param {string} value - The ISO date string to parse (e.g., "2023-10-25T14:30:00Z").
 * @returns {string} The formatted time string, or the original value if invalid.
 */
export function formatTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

/**
 * Calculates the duration in minutes between two ISO date strings.
 * Returns 0 if the dates are invalid or if the end time is before the start time.
 *
 * @param {string} start - The ISO date string for the start time.
 * @param {string} end - The ISO date string for the end time.
 * @returns {number} The duration in minutes, minimum of 1 for valid ranges.
 */
export function getDurationMinutes(start: string, end: string): number {
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();
  if (Number.isNaN(startTime) || Number.isNaN(endTime) || endTime < startTime) {
    return 0;
  }

  return Math.max(1, Math.round((endTime - startTime) / 60_000));
}

/**
 * Calculates the total duration in minutes of a complete journey plan,
 * from the departure of the first leg to the arrival of the last leg.
 *
 * @param {JourneyPlan} plan - The journey plan containing the legs of the trip.
 * @returns {number} The total duration in minutes, or 0 if legs are missing.
 */
export function getItineraryDurationMinutes(plan: JourneyPlan): number {
  const firstLeg = plan.legs[0];
  const lastLeg = plan.legs[plan.legs.length - 1];
  if (!firstLeg || !lastLeg) return 0;
  return getDurationMinutes(
    firstLeg.departureDateTime,
    lastLeg.arrivalDateTime,
  );
}

/**
 * Formats a duration in minutes into a human-readable string based on business rules.
 * - Under 1 min: "< 1 min"
 * - Under 60 mins: Shows minutes only (e.g., "24 mins")
 * - 60 mins or more: Shows total hours and exact remaining minutes (e.g., "25 hrs 32 mins")
 *
 * @param {number} minutes - The total duration in minutes.
 * @returns {string} A formatted string representing the duration.
 */
export function formatDuration(minutes: number): string {
  if (minutes < 1) return '< 1 min';

  const plural = (value: number, unit: string) =>
    `${value} ${unit}${value === 1 ? '' : 's'}`;

  if (minutes < 60) return plural(minutes, 'min');

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (mins === 0) return plural(hours, 'hr');
  return `${plural(hours, 'hr')} ${plural(mins, 'min')}`;
}

/**
 * Formats a transport mode string for display by replacing underscores with spaces.
 *
 * @param {string} mode - The raw transport mode string (e.g., "rail_replacement_bus").
 * @returns {string} The formatted display string (e.g., "rail replacement bus").
 */
export function formatMode(mode: string): string {
  return formatTransportMode(mode);
}

/**
 * Formats the overall time range of a journey plan as a string (e.g., "14:30 - 16:45").
 *
 * @param {JourneyPlan} plan - The journey plan to extract times from.
 * @returns {string} A formatted string showing the start and end times, or "Time unavailable".
 */
export function formatItineraryTimeRange(plan: JourneyPlan): string {
  const firstLeg = plan.legs[0];
  const lastLeg = plan.legs[plan.legs.length - 1];
  if (!firstLeg || !lastLeg) return 'Time unavailable';

  return `${formatTime(firstLeg.departureDateTime)} - ${formatTime(lastLeg.arrivalDateTime)}`;
}

/**
 * Generates a comma-separated overview string describing the sequence of a journey's legs.
 * Handles specific formatting for walking legs vs transit legs.
 *
 * @param {JourneyLeg[]} legs - An array of journey legs.
 * @returns {string} A readable overview string (e.g., "10 min walk, X1 bus, train").
 */
export function formatTransferOverview(legs: JourneyLeg[]): string {
  return legs
    .map((leg) => {
      if (leg.mode === 'walk') {
        return `${getDurationMinutes(leg.departureDateTime, leg.arrivalDateTime)} min walk`;
      }

      if (leg.lineName) {
        return `${leg.lineName} ${formatMode(leg.mode)}`;
      }

      return formatMode(leg.mode);
    })
    .join(', ');
}

export interface StepSummaryWalkItem {
  key: string;
  kind: 'walk';
  label: string;
  ariaLabel: string;
}

export interface StepSummaryServiceItem {
  key: string;
  kind: 'service';
  label: string;
  ariaLabel: string;
  mode: string;
  modeLabel: string;
  colors?: LineColors;
}

export type StepSummaryItem = StepSummaryWalkItem | StepSummaryServiceItem;

export function buildStepSummaryItems(legs: JourneyLeg[]): StepSummaryItem[] {
  return legs.map((leg, index) => {
    if (leg.mode === 'walk') {
      const durationLabel = formatDuration(
        getDurationMinutes(leg.departureDateTime, leg.arrivalDateTime),
      );

      return {
        key: `${index}-walk`,
        kind: 'walk',
        label: durationLabel,
        ariaLabel: `Walk for ${durationLabel}`,
      };
    }

    const modeLabel = titleCaseTransportMode(leg.mode);
    const isOperatorHeadsignMode =
      leg.headsign && OPERATOR_HEADSIGN_MODES.has(leg.mode);

    // For modes that use operator+headsign, use operator name instead of line name
    let serviceLabel: string;
    if (isOperatorHeadsignMode) {
      serviceLabel = leg.operator?.name ?? modeLabel;
    } else {
      serviceLabel = resolveJourneyServiceLabel(leg);
    }

    return {
      key: `${index}-service`,
      kind: 'service',
      label: serviceLabel,
      ariaLabel:
        serviceLabel === modeLabel ? modeLabel : `${modeLabel} ${serviceLabel}`,
      mode: leg.mode,
      modeLabel,
      colors: leg.line?.colors,
    };
  });
}

/**
 * Transforms a journey plan into an array of normalized itinerary steps
 * for use in the UI step-by-step view.
 *
 * @param {JourneyPlan} plan - The journey plan to transform.
 * @returns {ItineraryStep[]} An array of structured itinerary steps.
 */
export function buildItinerarySteps(
  plan: JourneyPlan,
  websiteBaseHref: string | null = null,
): ItineraryStep[] {
  return buildItineraryStepsInternal(plan, {
    formatMode,
    getDurationMinutes,
    websiteBaseHref,
  });
}

/**
 * Generates a unique key for a journey plan, typically used for React list mapping.
 * Combines the first departure time, last arrival time, and the array index.
 *
 * @param {JourneyPlan} plan - The journey plan.
 * @param {number} index - The index of the plan in the list.
 * @returns {string} A unique string key.
 */
export function buildPlanKey(plan: JourneyPlan, index: number): string {
  const firstLeg = plan.legs[0];
  const lastLeg = plan.legs[plan.legs.length - 1];
  return `${firstLeg?.departureDateTime ?? 'none'}-${lastLeg?.arrivalDateTime ?? 'none'}-${index}`;
}

/**
 * Capitalizes the first letter of a string.
 *
 * @param {string} value - The string to transform.
 * @returns {string} The string with its first letter capitalized.
 */
export function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Formats a date in local ISO format (YYYY-MM-DD).
 * @param date
 * @returns
 */
function formatLocalISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Adds a number of days to a date.
 * @param date
 * @param days
 * @returns
 */
function addLocalDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}
/**
 * Formats a date as a long date string (e.g., "1 January 2026").
 * @param date
 * @returns
 */
function formatDateLabel(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * Formats the local start date of a journey plan.
 * @param {JourneyPlan} plan - The journey plan to get the local start date from.
 * @returns
 */
export function getItineraryLocalStartDate(plan: JourneyPlan): string | null {
  const firstLeg = plan.legs[0];
  if (!firstLeg) return null;

  const start = new Date(firstLeg.departureDateTime);
  if (Number.isNaN(start.getTime())) return null;

  return formatLocalISODate(start);
}

export interface ItineraryStartBannerTextOptions {
  plan: JourneyPlan;
  /**
   * The date the user searched for, in local `YYYY-MM-DD` format.
   * (Matches the journey planner form date value.)
   */
  searchedDate?: string | null;
  /**
   * When true, no banner should be shown.
   * Intended as "journey start day is same as searched day".
   */
  isSameDay?: boolean;
  now?: Date;
}

/**
 * Formats the text for the itinerary start banner.
 * @param {ItineraryStartBannerTextOptions} options - The options for the itinerary start banner text.
 * @returns {string | null} The formatted text for the itinerary start banner.
 */
export function getItineraryStartBannerText({
  plan,
  searchedDate,
  isSameDay = false,
  now = new Date(),
}: ItineraryStartBannerTextOptions): string | null {
  if (!searchedDate) return null;
  if (isSameDay) return null;

  const firstLeg = plan.legs[0];
  if (!firstLeg) return null;

  const start = new Date(firstLeg.departureDateTime);
  if (Number.isNaN(start.getTime())) return null;

  const journeyDate = formatLocalISODate(start);
  const today = formatLocalISODate(now);

  const startTime = start.getTime();
  const nowTime = now.getTime();

  // Journey departs today on a real clock.
  if (journeyDate === today) {
    return startTime < nowTime
      ? 'This journey started today'
      : 'This journey starts today';
  }

  // Only use "tomorrow/yesterday" labels when the user searched for today.
  if (searchedDate === today) {
    const searchedMidnight = new Date(`${searchedDate}T00:00:00`);
    const tomorrowFromSearch = formatLocalISODate(
      addLocalDays(searchedMidnight, 1),
    );
    const yesterdayFromSearch = formatLocalISODate(
      addLocalDays(searchedMidnight, -1),
    );

    if (journeyDate === tomorrowFromSearch) {
      return 'This journey starts tomorrow';
    }

    if (journeyDate === yesterdayFromSearch) {
      return 'This journey started yesterday';
    }
  }

  // Any other date
  const parsedJourneyMidnight = new Date(`${journeyDate}T00:00:00`);
  return startTime < nowTime
    ? `This journey started ${formatDateLabel(parsedJourneyMidnight)}`
    : `This journey starts ${formatDateLabel(parsedJourneyMidnight)}`;
}
